import { useState } from 'react';
import axios from 'axios';

/**
 * Hook for generating assessment links for new treatments
 * 
 * @param {string} baseUrl - Backend API base URL (e.g., 'https://api.virtus-one.com')
 * @param {string} token - Auth token to send in Authorization header
 * @returns {object} - { generateAssessmentLink, loading, error, assessmentLink }
 */
export const useGenerateAssessmentLink = (baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000', token = null) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assessmentLink, setAssessmentLink] = useState(null);

  const generateAssessmentLink = async (patientId, diagnosisIdentifier, createNewTreatment = false, isShortForm = false, treatmentId = null) => {
    setLoading(true);
    setError(null);
    setAssessmentLink(null);

    try {
      console.log('[useGenerateAssessmentLink] Generating link for:', {
        patientId,
        diagnosisIdentifier,
        createNewTreatment,
        isShortForm,
        treatmentId,
      });

      const response = await axios.post(
        `${baseUrl}/assessment/generate-link/`,
        {
          patient_id: patientId,
          diagnosis_identifier: diagnosisIdentifier,
          create_new_treatment: createNewTreatment,
          is_short_form: isShortForm,
          ...(treatmentId ? { treatment_id: treatmentId } : {}),
        },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Token ${token}` } : {}),
          },
        }
      );

      console.log('[useGenerateAssessmentLink] Response:', response.data);

      if (response.data.assessment_url) {
        // Frontend URL should always use port 3000, not the backend port
        const frontendBase = process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000';
        const fullUrl = `${frontendBase}${response.data.assessment_url}`;
        setAssessmentLink(fullUrl);
        return {
          url: fullUrl,
          sessionId: response.data.session_id,
          data: response.data,
        };
      }
    } catch (err) {
      console.error('[useGenerateAssessmentLink] Error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to generate assessment link';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    generateAssessmentLink,
    loading,
    error,
    assessmentLink,
  };
};

export default useGenerateAssessmentLink;
